/**
 * Topography 3D generation (topo round). The 3D scene always shows the FULL graded
 * surface (every modifier applied — 3D is unphased; phase reads are the 2D views'
 * job, and the 3D phase treatment is the existing color overlay). The solid is a
 * CLOSED earth mass: graded TIN on top, a vertical skirt around the hull, and a flat
 * base `baseDepthFt` below the low point — closed on purpose, so the section cut,
 * the vertical pick, and spot elevations all read it like any other body and the
 * below-grade earth poché falls out of the same machinery as every other cut.
 *
 * SURFACE REGIONS drape as material buckets (the roof surface-override pattern):
 * top triangles bucket by the LAST live region containing their centroid — with the
 * region boundaries densified into the triangulation, triangles align to the seams.
 * No overrides ⇒ one solid, exactly the plain terrain. Contour polylines ride
 * `mesh.edges` so the terrain reads in 3D and its contours project into elevations
 * through the existing beyond-cut wireframe pass.
 */
import earcut from 'earcut'
import type { Pt2, ResolvedElement } from '@aj/bim-kernel'
import type { ElementSolid } from '../wall/generate3d'
import {
  buildExistingTin, buildGradedTin, deriveContours, hullRing,
  regionMaterialAt, resolveTopoModifiers, topoBreaklinesOf, topoPointsOf,
  type ResolvedTopoModifier, type TerrainCutter, type TopoTin,
} from './tin'

interface MeshAcc { pos: number[]; nrm: number[]; idx: number[]; vc: number }

/** TERRAIN EDGE GUARD (2026-08-23): how deep the INVISIBLE, collision-only skirt
 *  hangs below the terrain's own low point — see the `|walkskirt` block in
 *  topoSolid. Independent of `baseDepthFt` (the real, shallow, presentation
 *  skirt depth) on purpose: this one only ever matters to walk-mode collision. */
const WALK_COLLISION_SKIRT_DEPTH_FT = 1000

function pushTri(m: MeshAcc, a: number[], b: number[], c: number[]): void {
  const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2]
  const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2]
  let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
  const len = Math.hypot(nx, ny, nz) || 1
  nx /= len; ny /= len; nz /= len
  m.pos.push(...a, ...b, ...c)
  m.nrm.push(nx, ny, nz, nx, ny, nz, nx, ny, nz)
  m.idx.push(m.vc, m.vc + 1, m.vc + 2)
  m.vc += 3
}

/** The graded (all-modifiers) TIN + resolved modifiers for an element, or null. */
export function topoGradedFor(
  el: ResolvedElement,
  byId?: Map<string, ResolvedElement>,
): { existing: TopoTin; graded: TopoTin; live: ResolvedTopoModifier[] } | null {
  const existing = buildExistingTin(topoPointsOf(el), topoBreaklinesOf(el))
  if (!existing) return null
  const live = resolveTopoModifiers(el, byId)
  return { existing, graded: buildGradedTin(existing, live), live }
}

export function topoSolid(
  el: ResolvedElement,
  byId?: Map<string, ResolvedElement>,
  // TERRAIN FOUNDATION CUT round (Matt, 2026-08-15): foundation prisms (wall
  // bodies, stem walls, footings) boolean their shape out of the earth mass —
  // gathered from the built solids by the pipeline's synthetic pass, folded
  // into buildGradedTin's OWN single seam pass (cutting the already-seamed
  // graded mesh as a second surgery aliased pad-seam duplicates and tore).
  // The per-element pass builds the UNCUT terrain (cutters need every other
  // solid first); footings keep deriving from the uncut surface via
  // topoGradedFor, so there is no cycle.
  cutters?: TerrainCutter[],
): ElementSolid[] | null {
  const built = topoGradedFor(el, byId)
  if (!built) return null
  const { existing, live } = built
  const graded = cutters?.length
    ? buildGradedTin(existing, live, live, cutters)
    : built.graded
  const baseDepth = typeof el.params.baseDepthFt === 'number' && el.params.baseDepthFt > 0
    ? (el.params.baseDepthFt as number) : 5
  let zMin = Infinity, zMax = -Infinity
  for (const z of graded.zs) { if (z < zMin) zMin = z; if (z > zMax) zMax = z }
  const zBase = zMin - baseDepth

  const defaultMat = typeof el.params.surfaceMaterial === 'string' && el.params.surfaceMaterial
    ? (el.params.surfaceMaterial as string) : 'default'
  const buckets = new Map<string, MeshAcc>()
  const bucketFor = (mat: string): MeshAcc => {
    let b = buckets.get(mat)
    if (!b) { b = { pos: [], nrm: [], idx: [], vc: 0 }; buckets.set(mat, b) }
    return b
  }
  bucketFor(defaultMat)   // primary bucket first — deterministic solid order

  // top surface, bucketed by draped region material at the triangle centroid.
  // WINDING (review fix): delaunator's "counterclockwise" is SCREEN (y-down) space —
  // in this codebase's y-up world XY its triangles (and hull) come out CLOCKWISE, so
  // emit a,c,b to face the tops UP (front-side materials cull backfaces).
  const v3 = (i: number): number[] => [graded.pts[i][0], graded.pts[i][1], graded.zs[i]]
  for (let i = 0; i + 2 < graded.tris.length; i += 3) {
    const [a, b, c] = [graded.tris[i], graded.tris[i + 1], graded.tris[i + 2]]
    const cen: Pt2 = [
      (graded.pts[a][0] + graded.pts[b][0] + graded.pts[c][0]) / 3,
      (graded.pts[a][1] + graded.pts[b][1] + graded.pts[c][1]) / 3,
    ]
    const mat = regionMaterialAt(live, cen) ?? defaultMat
    const m = bucketFor(mat)
    pushTri(m, v3(a), v3(c), v3(b))
  }

  // skirt + flat base close the mass (hull is CW in y-up — wind the skirt outward)
  const hull = hullRing(graded)
  const hullZ = graded.hull.map((i) => graded.zs[i])
  const mBase = bucketFor(defaultMat)
  for (let i = 0; i < hull.length; i++) {
    const j = (i + 1) % hull.length
    const a = [hull[i][0], hull[i][1], hullZ[i]]
    const b = [hull[j][0], hull[j][1], hullZ[j]]
    const a0 = [hull[i][0], hull[i][1], zBase]
    const b0 = [hull[j][0], hull[j][1], zBase]
    pushTri(mBase, a, b0, a0)
    pushTri(mBase, a, b, b0)
  }
  const flat = hull.flatMap((p) => [p[0], p[1]])
  const baseTris = earcut(flat)
  for (let i = 0; i + 2 < baseTris.length; i += 3) {
    const [a, b, c] = [baseTris[i], baseTris[i + 1], baseTris[i + 2]]
    // wind downward
    pushTri(mBase,
      [hull[a][0], hull[a][1], zBase],
      [hull[c][0], hull[c][1], zBase],
      [hull[b][0], hull[b][1], zBase])
  }

  // 3D feature lines: the hull's top edge + derived contours (the terrain read)
  const edges: number[] = []
  for (let i = 0; i < hull.length; i++) {
    const j = (i + 1) % hull.length
    edges.push(hull[i][0], hull[i][1], hullZ[i], hull[j][0], hull[j][1], hullZ[j])
  }
  const minor = typeof el.params.minorIntervalFt === 'number' && el.params.minorIntervalFt > 0
    ? (el.params.minorIntervalFt as number) : 1
  const major = typeof el.params.majorIntervalFt === 'number' && el.params.majorIntervalFt > 0
    ? (el.params.majorIntervalFt as number) : 5
  for (const c of deriveContours(graded, minor, major)) {
    if (!c.major) continue   // 3D shows the major set only — the minor set is plan detail
    for (const line of c.lines) {
      for (let i = 0; i + 1 < line.length; i++) {
        edges.push(line[i][0], line[i][1], c.zFt, line[i + 1][0], line[i + 1][1], c.zFt)
      }
    }
  }

  // TERRAIN EDGE GUARD r2 (2026-08-23, Matt: "I am still able to walk off the
  // edge" — the r1 client-side fix in sceneStore.js tried to re-derive boundary
  // edges from the flattened triangle buffer and was WRONG, because this solid
  // is already a closed mass: the visible skirt above (baseDepth, default 5')
  // shares no vertex INDICES with the top surface it hangs from — pushTri never
  // welds — so an "edge used by exactly one triangle" test found boundary
  // edges almost everywhere, not just the true perimeter, and even where it
  // WAS roughly right, 5' is nowhere near deep enough: a walking capsule only
  // grazes the top sliver of that skirt as it crosses the edge and a normal
  // step can clear it in one frame.
  //
  // Fixed at the SOURCE instead, reusing the exact hull/hullZ already computed
  // above for the real (correctly-shallow, presentation-depth) skirt — no
  // re-derivation, no ambiguity about what the true edge is. r2a tried carrying
  // this as a SECOND ElementSolid (its own `|walkskirt` groupKey) — reverted
  // same round: `category: 'topo'` made it visible to every OTHER `category ===
  // 'topo'` consumer in plan.ts/section.ts too (they filter at the RESOLVED-
  // ELEMENT level, driving their own independent TIN re-slice — entirely blind
  // to `skipProjectedOutline`, which only gates the generic solids-array 2D
  // pass), so it leaked into 2D plan cuts (topo.test.ts caught it). Riding as
  // inert METADATA on the primary solid instead — `walkSkirt` is a field no 2D
  // consumer has ever heard of, so nothing there can accidentally pick it up;
  // `buildGroupChunk` (pipeline.ts) copies it onto the resulting GroupChunk,
  // and sceneStore.js reads `chunk.walkSkirt` to build the invisible collider,
  // entirely outside the solids/ElementSolid pipeline every 2D view walks.
  const wAcc: MeshAcc = { pos: [], nrm: [], idx: [], vc: 0 }
  const deepBase = zMin - WALK_COLLISION_SKIRT_DEPTH_FT
  for (let i = 0; i < hull.length; i++) {
    const j = (i + 1) % hull.length
    const a = [hull[i][0], hull[i][1], hullZ[i]]
    const b = [hull[j][0], hull[j][1], hullZ[j]]
    const a0 = [hull[i][0], hull[i][1], deepBase]
    const b0 = [hull[j][0], hull[j][1], deepBase]
    pushTri(wAcc, a, b0, a0)
    pushTri(wAcc, a, b, b0)
  }
  const walkSkirt = wAcc.vc > 0
    ? { positions: new Float32Array(wAcc.pos), indices: new Uint32Array(wAcc.idx) }
    : undefined

  const out: ElementSolid[] = []
  let n = 0
  for (const [mat, acc] of buckets) {
    if (acc.vc === 0) continue
    const primary = out.length === 0
    out.push({
      elementId: el.id,
      category: 'topo',
      outlines: primary ? [hull] : [],
      zBase,
      zTop: zMax,
      materialId: mat,
      // <cat>|<levelId>|… like every generator — sceneStore's level filter reads
      // segment [1] as the level id (review fix: el.id there broke active-level-only)
      groupKey: `topo|${el.levelId}|${el.id}${primary ? '' : `|surf${n}`}`,
      pieces: [],
      placements: [],
      openingInstances: [],
      mesh: {
        positions: new Float32Array(acc.pos),
        normals: new Float32Array(acc.nrm),
        indices: new Uint32Array(acc.idx),
        edges: primary ? edges : [],
      },
      meshOnly: true,
      skipProjectedOutline: true,
      // TERRAIN EDGE GUARD r2 — see the comment above; primary chunk only
      walkSkirt: primary ? walkSkirt : undefined,
    })
    n++
  }
  return out.length ? out : null
}
